import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import api from '../api/axios';
import { buildWsUrl } from '../utils/ws';
import { isNativeApp } from '../native/runtime';

/**
 * MailboxContext
 *  • Хранит все уведомления пользователя
 *  • Отслеживает новые (is_read === false)
 *  • Поднимает флаг latestNotification, чтобы UI мог показать pop‑up
 *  • Даёт метод markAsRead(id) – POST /notifications/:id/read/
 */

export const MailboxContext = createContext();

const MAILBOX_NOTIFIED_STORAGE_KEY = 'mailbox_notified';
const MAILBOX_READ_ALL_PAGE_SIZE = 100;
const MAILBOX_READ_ALL_MAX_PAGES = 200;
const MAILBOX_READ_ALL_BATCH_SIZE = 25;
const MAILBOX_FALLBACK_POLL_INTERVAL_MS = 30 * 1000;
const MAILBOX_REALTIME_SAFETY_POLL_INTERVAL_MS = 5 * 60 * 1000;

function notificationStorageKey(notificationOrId) {
  const rawId = typeof notificationOrId === 'object'
    ? notificationOrId?.id
    : notificationOrId;
  const value = rawId == null ? '' : String(rawId).trim();
  return value || '';
}

function readNotifiedStorage() {
  if (typeof localStorage === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(MAILBOX_NOTIFIED_STORAGE_KEY) || '[]';
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.map((id) => notificationStorageKey(id)).filter(Boolean));
  } catch {
    return new Set();
  }
}

function persistNotifiedStorage(ids) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(MAILBOX_NOTIFIED_STORAGE_KEY, JSON.stringify(Array.from(ids)));
  } catch {
    // Non-critical: duplicate toast prevention still works for the current session.
  }
}

function chunkArray(items, size) {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

function compareNotificationsDesc(a, b) {
  const aTime = Date.parse(a?.created_at || '');
  const bTime = Date.parse(b?.created_at || '');
  const safeATime = Number.isFinite(aTime) ? aTime : 0;
  const safeBTime = Number.isFinite(bTime) ? bTime : 0;
  if (safeBTime !== safeATime) return safeBTime - safeATime;
  return Number(b?.id || 0) - Number(a?.id || 0);
}

function sortNotificationsDesc(items) {
  return [...(Array.isArray(items) ? items : [])].sort(compareNotificationsDesc);
}

function latestCachedNotificationId(items) {
  const ids = (Array.isArray(items) ? items : [])
    .map((item) => Number(item?.id || 0))
    .filter((id) => Number.isFinite(id) && id > 0);
  return ids.length > 0 ? Math.max(...ids) : null;
}

export const MailboxProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const mailboxWsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const hasSeededNotificationsRef = useRef(false);
  const providerStartedAtRef = useRef(Date.now());
  const nativeRuntimeRef = useRef(isNativeApp());
  const nativeAppActiveRef = useRef(true);
  const suppressNextNativePollToastRef = useRef(false);
  const [mailboxWsConnected, setMailboxWsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Чтобы не дублировать pop‑up для одного и того же уведомления даже после перезагрузки страницы
  const notifiedIdsRef = useRef(readNotifiedStorage());

  // Последнее непрочитанное уведомление, которое мы ещё не показали пользователю
  const [latestNotification, setLatestNotification] = useState(null);

  const markNotificationsNotified = useCallback((items) => {
    const list = Array.isArray(items) ? items : [items];
    let changed = false;

    list.forEach((item) => {
      const key = notificationStorageKey(item);
      if (!key || notifiedIdsRef.current.has(key)) return;
      notifiedIdsRef.current.add(key);
      changed = true;
    });

    if (changed) {
      persistNotifiedStorage(notifiedIdsRef.current);
    }

    return changed;
  }, []);

  const publishLatestNotification = useCallback((note) => {
    if (!note || note.is_read) return;
    const key = notificationStorageKey(note);
    if (!key || notifiedIdsRef.current.has(key)) return;

    notifiedIdsRef.current.add(key);
    persistNotifiedStorage(notifiedIdsRef.current);
    if (!nativeRuntimeRef.current || nativeAppActiveRef.current) {
      setLatestNotification(note);
    }
  }, []);

  const mergeNotificationToCache = useCallback((note) => {
    if (!note || note.is_read) return;
    queryClient.setQueryData(['mailbox'], (oldData) => {
      const current = Array.isArray(oldData) ? oldData : [];
      const exists = current.some((n) => Number(n?.id || 0) === Number(note?.id || 0));
      if (exists) return current;
      return sortNotificationsDesc([note, ...current]);
    });
    queryClient.setQueriesData({ queryKey: ['notifications'] }, (oldData) => {
      if (!oldData || !Array.isArray(oldData.pages)) return oldData;
      const noteId = Number(note?.id || 0);
      const existsAnywhere = oldData.pages.some((page) => {
        const results = Array.isArray(page?.results) ? page.results : [];
        return results.some((n) => Number(n?.id || 0) === noteId);
      });
      if (existsAnywhere) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page, index) => {
          const results = Array.isArray(page?.results) ? page.results : [];
          if (index !== 0) return page;
          return {
            ...page,
            total: Number(page?.total || 0) + 1,
            results: sortNotificationsDesc([note, ...results]),
          };
        }),
      };
    });
  }, [queryClient]);

  const markNotificationReadInCache = useCallback((notificationId) => {
    const targetId = Number(notificationId || 0);
    if (!targetId) return;
    let removedUnreadNotification = false;
    queryClient.setQueryData(['mailbox'], (oldData) => {
      const current = Array.isArray(oldData) ? oldData : [];
      removedUnreadNotification = current.some(
        (n) => Number(n?.id || 0) === targetId && !n?.is_read
      );
      return current.filter((n) => Number(n?.id || 0) !== targetId);
    });
    if (removedUnreadNotification) {
      setUnreadCount((count) => Math.max(0, Number(count || 0) - 1));
    }
    queryClient.setQueriesData({ queryKey: ['notifications'] }, (oldData) => {
      if (!oldData || !Array.isArray(oldData.pages)) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page) => {
          const results = Array.isArray(page?.results) ? page.results : [];
          let changed = false;
          const nextResults = results.map((item) => {
            if (Number(item?.id || 0) !== targetId || item?.is_read) return item;
            changed = true;
            return { ...item, is_read: true };
          });
          return changed ? { ...page, results: nextResults } : page;
        }),
      };
    });
  }, [queryClient]);

  const markAllNotificationsReadInCache = useCallback(() => {
    setUnreadCount(0);
    setLatestNotification(null);
    queryClient.setQueryData(['mailbox'], []);
    queryClient.setQueriesData({ queryKey: ['notifications'] }, (oldData) => {
      if (!oldData || !Array.isArray(oldData.pages)) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          results: (Array.isArray(page?.results) ? page.results : []).map((item) => ({
            ...item,
            is_read: true,
          })),
        })),
      };
    });
  }, [queryClient]);

  const upsertNotificationInCache = useCallback((note) => {
    if (!note || Number(note?.id || 0) <= 0) return;
    if (note.is_read) {
      markNotificationReadInCache(note.id);
    } else {
      queryClient.setQueryData(['mailbox'], (oldData) => {
        const current = Array.isArray(oldData) ? oldData : [];
        let found = false;
        const next = current.map((item) => {
          if (Number(item?.id || 0) !== Number(note?.id || 0)) return item;
          found = true;
          return { ...item, ...note };
        });
        return sortNotificationsDesc(found ? next : [note, ...next]);
      });
    }
    queryClient.setQueriesData({ queryKey: ['notifications'] }, (oldData) => {
      if (!oldData || !Array.isArray(oldData.pages)) return oldData;

      const noteId = Number(note?.id || 0);
      const existsAnywhere = oldData.pages.some((page) => {
        const results = Array.isArray(page?.results) ? page.results : [];
        return results.some((item) => Number(item?.id || 0) === noteId);
      });

      const nextPages = oldData.pages.map((page, index) => {
        const results = Array.isArray(page?.results) ? page.results : [];
        let changed = false;
        const nextResults = results.map((item) => {
          if (Number(item?.id || 0) !== noteId) return item;
          changed = true;
          return { ...item, ...note };
        });
        if (changed) return { ...page, results: sortNotificationsDesc(nextResults) };
        if (!existsAnywhere && index === 0 && !note.is_read) {
          return {
            ...page,
            total: Number(page?.total || 0) + 1,
            results: sortNotificationsDesc([note, ...results]),
          };
        }
        return { ...page, results: sortNotificationsDesc(results) };
      });

      return { ...oldData, pages: nextPages };
    });
  }, [markNotificationReadInCache, queryClient]);

  /* ───── Initial load and full mailbox reconciliation ───── */
  const {
    data: notifications = [],
    refetch,
    isSuccess: mailboxQueryReady,
    dataUpdatedAt: unreadUpdatedAt,
  } = useQuery({
    queryKey: ['mailbox'],
    queryFn: async () => {
      // ?unread=1 → только непрочитанные
      const res = await api.get('/notifications/', { params: { unread: 1, limit: 100 } });
      // Сервер может вернуть либо массив, либо объект с results
      const payload = res.data;
      const results = Array.isArray(payload) ? payload : (payload.results || []);
      const total = Array.isArray(payload) ? results.length : Number(payload?.total ?? results.length);
      setUnreadCount(Number.isFinite(total) ? Math.max(0, total) : results.length);
      return sortNotificationsDesc(results);
    },
    refetchInterval: false,
    staleTime: 0,                   // сразу считается устаревшим
    refetchOnWindowFocus: true,
  });

  const checkUnreadSummary = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread-summary/', {
        meta: { suppressGlobalError: true },
      });
      const serverUnreadCount = Math.max(0, Number(res?.data?.unread_count || 0));
      const rawLatestId = Number(res?.data?.latest_unread_id || 0);
      const serverLatestId = Number.isFinite(rawLatestId) && rawLatestId > 0
        ? rawLatestId
        : null;
      const cachedNotifications = queryClient.getQueryData(['mailbox']);
      const cachedLatestId = latestCachedNotificationId(cachedNotifications);

      if (
        serverUnreadCount !== Math.max(0, Number(unreadCount || 0))
        || serverLatestId !== cachedLatestId
      ) {
        await refetch();
      }
    } catch {
      // The next summary tick, focus refresh, native resume, or websocket
      // event will reconcile state. A background failure must stay silent.
    }
  }, [queryClient, refetch, unreadCount]);

  // Poll only the small change detector. The complete unread feed is fetched
  // when the detector finds a difference or another established lifecycle
  // event explicitly requests reconciliation.
  useEffect(() => {
    if (!mailboxQueryReady) return undefined;
    const intervalMs = mailboxWsConnected
      ? MAILBOX_REALTIME_SAFETY_POLL_INTERVAL_MS
      : MAILBOX_FALLBACK_POLL_INTERVAL_MS;
    const timer = window.setInterval(() => {
      void checkUnreadSummary();
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [checkUnreadSummary, mailboxQueryReady, mailboxWsConnected]);

  // The unread query is also the native-app resume fallback. Keep the full
  // paginated Mailbox cache in sync with anything it discovers, otherwise the
  // badge can update while an already-cached Mailbox list remains unchanged.
  useEffect(() => {
    notifications.forEach((note) => mergeNotificationToCache(note));
  }, [mergeNotificationToCache, notifications]);

  useEffect(() => {
    if (!nativeRuntimeRef.current) return undefined;

    const handleNativeAppState = (event) => {
      const isActive = event?.detail?.isActive !== false;
      nativeAppActiveRef.current = isActive;
      suppressNextNativePollToastRef.current = true;

      if (!isActive) {
        // A native system notification owns background presentation. Do not
        // leave an in-app copy waiting underneath it.
        setLatestNotification(null);
        return;
      }

      // Reconcile the mailbox on resume. The first result is marked as already
      // surfaced because iOS owned presentation while the app was inactive.
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      refetch();
    };

    window.addEventListener('vg:native-app-state', handleNativeAppState);
    return () => {
      window.removeEventListener('vg:native-app-state', handleNativeAppState);
    };
  }, [queryClient, refetch]);

  useEffect(() => {
    let isDisposed = false;

    const clearReconnect = () => {
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
      }
      reconnectTimerRef.current = null;
    };

    const closeSocket = () => {
      const ws = mailboxWsRef.current;
      mailboxWsRef.current = null;
      if (!ws) return;
      try {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        ws.close();
      } catch {
        // ignore
      }
    };

    const scheduleReconnect = () => {
      if (isDisposed) return;
      if (reconnectTimerRef.current) return;

      const attempt = reconnectAttemptRef.current || 0;
      const delayMs = Math.min(20_000, 1000 * (2 ** attempt));
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        connect();
      }, delayMs);
      reconnectAttemptRef.current = attempt + 1;
    };

    const connect = () => {
      if (isDisposed) return;
      clearReconnect();
      closeSocket();
      setMailboxWsConnected(false);

      let ws;
      try {
        ws = new WebSocket(buildWsUrl('/ws/mailbox/'));
      } catch {
        scheduleReconnect();
        return;
      }

      mailboxWsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptRef.current = 0;
        setMailboxWsConnected(true);
      };

      ws.onmessage = (evt) => {
        let payload = null;
        try {
          payload = JSON.parse(evt?.data || '{}');
        } catch {
          payload = null;
        }
        if (!payload) return;

        const event = String(payload?.event || '').toLowerCase();
        const nextUnreadCount = Number(payload?.unread_count);

        if (event === 'notification.created' && payload?.notification) {
          mergeNotificationToCache(payload.notification);
          publishLatestNotification(payload.notification);
          // Dispatch for any component listening to real-time notifications
          window.dispatchEvent(new CustomEvent('vg:notification', { detail: payload.notification }));
        } else if (event === 'notification.updated' && payload?.notification) {
          upsertNotificationInCache(payload.notification);
        } else if (event === 'notification.read') {
          markNotificationReadInCache(payload?.notification_id);
        } else if (event === 'notification.read_all') {
          markAllNotificationsReadInCache();
        }

        // Realtime counts are authoritative. Apply them after local cache
        // reconciliation so a read event cannot accidentally decrement twice.
        if (Number.isFinite(nextUnreadCount)) {
          setUnreadCount(Math.max(0, nextUnreadCount));
        }

        // Best-effort full sync in background to keep payloads consistent with backend.
        refetch();
      };

      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          // ignore
        }
      };

      ws.onclose = () => {
        if (isDisposed) return;
        setMailboxWsConnected(false);
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      isDisposed = true;
      clearReconnect();
      closeSocket();
    };
  }, [markAllNotificationsReadInCache, mergeNotificationToCache, markNotificationReadInCache, publishLatestNotification, refetch, upsertNotificationInCache]);

  useEffect(() => {
    if (!mailboxQueryReady) return;

    if (!hasSeededNotificationsRef.current) {
      hasSeededNotificationsRef.current = true;
      markNotificationsNotified(notifications);
      return;
    }

    const newOnes = notifications.filter((n) => {
      const key = notificationStorageKey(n);
      if (!key || notifiedIdsRef.current.has(key)) return false;
      const createdAtMs = Date.parse(n?.created_at || '');
      return Number.isFinite(createdAtMs) && createdAtMs >= providerStartedAtRef.current - 5000;
    });
    const suppressNativePollToast = Boolean(
      nativeRuntimeRef.current
      && (
        !nativeAppActiveRef.current
        || suppressNextNativePollToastRef.current
      )
    );
    if (newOnes.length > 0) {
      markNotificationsNotified(newOnes);
      if (!suppressNativePollToast) {
        const latest = newOnes.reduce((prev, curr) =>
          compareNotificationsDesc(prev, curr) <= 0 ? prev : curr
        );
        setLatestNotification(latest);
      }
    }
    if (nativeRuntimeRef.current && nativeAppActiveRef.current) {
      suppressNextNativePollToastRef.current = false;
    }
  }, [mailboxQueryReady, markNotificationsNotified, notifications]);

  /* ───── Мутация "пометить как прочитанное" ───── */
  const { mutate: markAsRead } = useMutation({
    mutationFn: async (id) => api.post(`/notifications/${id}/read/`),
    onMutate: (id) => {
      markNotificationsNotified(id);
      setLatestNotification((current) =>
        notificationStorageKey(current) === notificationStorageKey(id) ? null : current
      );
    },
    onSuccess: (response, id) => {
      if (response?.data?.is_read && !response?.data?.preserved_for_action) {
        // A read in this device must update the shared unread count immediately;
        // waiting for the WebSocket or a refetch leaves the iOS icon badge stale.
        markNotificationReadInCache(id);
      }
      queryClient.invalidateQueries({ queryKey: ['mailbox'] });
    },
    onError: () => {
      // MailboxModal shows the read state optimistically. Restore the server
      // state when the mutation fails so the list and badge agree again.
      queryClient.invalidateQueries({ queryKey: ['mailbox'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllAsRead = useCallback(async () => {
    try {
      await api.post('/notifications/read-all/', null, {
        meta: { suppressGlobalError: true },
      });
      markAllNotificationsReadInCache();
      await refetch();
      return;
    } catch (bulkError) {
      const status = bulkError?.response?.status;
      if (status && ![404, 405, 500, 502, 503, 504].includes(Number(status))) {
        throw bulkError;
      }
    }

    let markedCount = 0;

    for (let pageIndex = 0; pageIndex < MAILBOX_READ_ALL_MAX_PAGES; pageIndex += 1) {
      const res = await api.get('/notifications/', {
        params: { unread: 1, limit: MAILBOX_READ_ALL_PAGE_SIZE, offset: 0 },
      });
      const payload = res?.data;
      const results = Array.isArray(payload) ? payload : (payload?.results || []);
      const ids = results
        .map((item) => Number(item?.id || 0))
        .filter((id) => id > 0);

      if (ids.length === 0) break;

      for (const batch of chunkArray(ids, MAILBOX_READ_ALL_BATCH_SIZE)) {
        const settled = await Promise.allSettled(
          batch.map((id) => api.post(`/notifications/${id}/read/`))
        );
        markedCount += settled.filter((item) => item.status === 'fulfilled').length;
      }

      if (ids.length < MAILBOX_READ_ALL_PAGE_SIZE) break;
    }

    markAllNotificationsReadInCache();
    await refetch();

    if (markedCount <= 0) {
      throw new Error('No unread mailbox notifications were marked as read.');
    }
  }, [markAllNotificationsReadInCache, refetch]);

  const contextValue = useMemo(
    () => ({
      notifications,
      unreadCount,
      unreadReady: mailboxQueryReady,
      unreadUpdatedAt,
      refetch,
      latestNotification,          // объект уведомления или null
      clearLatestNotification: () => setLatestNotification(null),
      markAsRead,                  // (id) => void
      markAllAsRead,
    }),
    [notifications, unreadCount, mailboxQueryReady, unreadUpdatedAt, refetch, latestNotification, markAsRead, markAllAsRead],
  );

  return (
    <MailboxContext.Provider value={contextValue}>
      {children}
    </MailboxContext.Provider>
  );
};

export const useMailbox = () => useContext(MailboxContext);
